import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { FiArrowRight, FiBell, FiCheck, FiDroplet, FiFolder, FiGift, FiLayers, FiMaximize, FiMonitor, FiPrinter, FiShield, FiSliders, FiSmartphone, FiZap } from "react-icons/fi";
import { Button, Field, InlineAlert, useToast } from "../print/components/ui";
import { API_BASE, sha256Hex } from "../print/api/client";
import { APP_STORE_URL, PLAY_STORE_URL } from "../print/lib/device";
import StoryScene from "./StoryScene";
import HowItWorks from "./HowItWorks";
import Reveal from "./Reveal";
import Seo from "./Seo";
import { mobileApp, organization, website } from "./schema";
import { SITE } from "./siteInfo";
import shops from "../data/shops.json";
import { CTA_LAYOUT_ID, CTA_TRANSITION, useCtaInNav } from "./ctaStore";

const PAGES_LAKH = shops.totalPagesPrinted ? (Math.floor(shops.totalPagesPrinted / 10000) / 10).toFixed(1) : null;

export default function HomePage() {
  return (
    <>
      <div className="site">
        <Seo path="/" jsonLd={[organization(), website(), mobileApp()]} />
        <Hero />
        <HowItWorks />
        <Features />
        <AppBand />
        <Partner />
        <div className="stickycta__space" aria-hidden="true" />
      </div>
      <MobileCta />
    </>
  );
}

function Hero() {
  const ctaInNav = useCtaInNav();
  return (
    <section className="hero" id="header">
      <div className="wrap hero__grid">
        <div className="hero__copy">
        <h1 className="hero__title">
          <span className="hero__line">
            <span>Upload now.</span>
          </span>
          <span className="hero__line">
            <span>It's printed before</span>
          </span>
          <span className="hero__line">
            <span>you reach the shop.</span>
          </span>
        </h1>
        <div className="hero__aside">
          <p className="hero__slogan">{SITE.tagline}</p>
          <p className="hero__sub">Send a PDF or photo to a xerox shop near your college, pay by UPI and collect it with your order number.</p>
          <div className="hero__ctas" data-cta-anchor>
            {ctaInNav ? (
              <span className="pa-btn pa-btn--primary pa-btn--lg hero__cta-ghost" aria-hidden="true">
                <FiPrinter /> <span>Print now</span>
              </span>
            ) : (
              <motion.div className="hero__cta-slot" layoutId={CTA_LAYOUT_ID} transition={CTA_TRANSITION}>
                <Link to="/print" className="pa-btn pa-btn--primary pa-btn--lg">
                  <FiPrinter /> <span>Print now</span>
                </Link>
              </motion.div>
            )}
            <Link to="/get-app" className="pa-btn pa-btn--secondary pa-btn--lg">
              <FiSmartphone /> <span>Get the app</span>
            </Link>
          </div>
          <ul className="hero__proof">
            <li>
              <strong>
                4.8<i className="hero__star">★</i>
              </strong>
              <span>rating on Google Play</span>
            </li>
            {PAGES_LAKH ? (
              <li>
                <strong>{PAGES_LAKH} lakh</strong>
                <span>pages printed so far</span>
              </li>
            ) : null}
          </ul>
        </div>
        </div>
        <div className="hero__stage">
          <StoryScene />
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { Icon: FiZap, title: "Straight to the printer", text: "The moment your payment goes through, the job is queued on the shop's printer. Nothing is forwarded on WhatsApp or copied to a pen drive." },
  { Icon: FiSliders, title: "Your settings travel with the file", text: "Copies, colour or black and white, one or both sides, page size and pages per sheet. The shop prints exactly what you chose." },
  { Icon: FiDroplet, title: "Colour only where it's needed", text: "The pages with colour are detected for you. The rest print in black and white and are billed at the black and white rate." },
  { Icon: FiShield, title: "Private and secure", text: "Files are encrypted on the way and removed after printing. Shop staff see only the print job, never your account." },
  { Icon: FiBell, title: "Live tracking", text: "Queue position, printing progress and a ready alert, in the app or on the website." },
  { Icon: FiLayers, title: "Many files, one order", text: "Mix up to 15 PDFs, JPGs and PNGs with different settings in one order and one payment. 50 MB per file." },
  { Icon: FiMonitor, title: "Works in your browser too", text: "Upload from a laptop on printanywhere.in, log in with an OTP only when you pay, and track it on your phone. One account everywhere." },
  { Icon: FiGift, title: "Rewards and coupons", text: "₹5 for you and a friend after their first print, reward points worth up to 75% of a bill, and coupons at checkout." },
];

function Features() {
  return (
    <section className="features" id="features">
      <div className="wrap">
        <Reveal className="sec-head">
          <h2>Everything a print job needs, nothing to explain at the counter</h2>
          <p>Built around how students actually print: last minute, from a phone, at the shop nearest the gate.</p>
        </Reveal>
        <div className="feat">
          {FEATURES.map(({ Icon, title, text }, i) => (
            <motion.div
              className="feat__item"
              key={title}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px -10% 0px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <Icon aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AppBand() {
  return (
    <section className="appband" id="app">
      <div className="wrap appband__grid">
        <Reveal className="appband__copy">
          <h2>The app does a little more</h2>
          <p>Everything the website does, plus the things that only make sense in your pocket.</p>
          <ul className="appband__list">
            <li>
              <FiMaximize />
              <div>
                <strong>Scan and print at the counter</strong>
                <span>Point the app at the shop's QR and print on the spot, no shop to pick.</span>
              </div>
            </li>
            <li>
              <FiBell />
              <div>
                <strong>A buzz when it's ready</strong>
                <span>Queue, printing and ready alerts as notifications, so you can wait anywhere.</span>
              </div>
            </li>
            <li>
              <FiFolder />
              <div>
                <strong>Your files, kept for next time</strong>
                <span>Reprint last week's notes in two taps. Orders and rewards sync with the website.</span>
              </div>
            </li>
            <li>
              <FiGift />
              <div>
                <strong>Rewards and coupons</strong>
                <span>
                  ₹5 for you and a friend after their first print, reward points worth up to 75% of a bill. <Link to="/refer">Refer a friend</Link>
                </span>
              </div>
            </li>
          </ul>
          <div className="appband__get">
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
              <img src="/img/playstore.webp" alt="Get it on Google Play" loading="lazy" width="150" height="46" />
            </a>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
              <img src="/img/applestore.webp" alt="Download on the App Store" loading="lazy" width="150" height="46" />
            </a>
            <div className="appband__qr">
              <QRCodeSVG value="https://printanywhere.in/get-app" size={76} fgColor="#15120f" level="M" />
              <span>Scan with your phone to install</span>
            </div>
          </div>
        </Reveal>
        <Reveal className="appband__phones" y={60} delay={0.15}>
          <div className="appband__duo">
            <picture className="appband__duo-back">
              <source srcSet="/img/s24ultra-web.webp" type="image/webp" />
              <img src="/img/s24ultra-web.webp" alt="The PrintAnywhere app on an Android phone, showing an order ready to pay" loading="lazy" width="900" height="1834" />
            </picture>
            <picture className="appband__duo-front">
              <source srcSet="/img/iphone15promax-web.webp" type="image/webp" />
              <img src="/img/iphone15promax-web.webp" alt="The PrintAnywhere app on an iPhone, showing an order ready to pay" loading="lazy" width="900" height="1856" />
            </picture>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const PARTNER_KEY = "enquirysendkaraychaseltrprintanywhereinwebsitevarunchkarabaherunaccepthonarnai";

export function Partner() {
  const toast = useToast();
  const [form, setForm] = useState({ name: "", email: "", mobileNumber: "", businessName: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'done' | 'exists' | 'error'

  const set = (k) => (e) => {
    const v = k === "mobileNumber" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(form.mobileNumber)) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    setStatus("sending");
    try {
      const ticket = await sha256Hex(PARTNER_KEY + form.mobileNumber);
      const res = await fetch(`${API_BASE}/web/enquiry/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", WebTicket: ticket },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), mobileNumber: form.mobileNumber, businessName: form.businessName.trim(), message: form.message.trim() }),
      });
      if (res.status === 200) setStatus("done");
      else if (res.status === 408) setStatus("exists");
      else setStatus("error");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section className="partner" id="partner">
      <div className="wrap partner__grid">
        <Reveal className="partner__copy">
          <h2>Own a xerox shop? Get paid jobs while you serve the counter</h2>
          <p>PrintAnywhere sends paid, pre-configured jobs straight to your printer. No chasing payments, no retyping settings, no WhatsApp back and forth.</p>
          <ul className="facts">
            <li>
              <FiCheck />
              <span>
                <strong>More customers.</strong> Students and professionals nearby find your shop in the app and on this site.
              </span>
            </li>
            <li>
              <FiCheck />
              <span>
                <strong>Paid before printing.</strong> Every job is paid online before it reaches your printer, with clear settlements.
              </span>
            </li>
            <li>
              <FiCheck />
              <span>
                <strong>Nothing new to buy.</strong> A small Windows program connects the printers you already have. We help you set it up.
              </span>
            </li>
          </ul>
        </Reveal>
        <Reveal as="form" className="partner__form paper" onSubmit={submit} y={40} delay={0.1}>
          <h3>Tell us about your shop</h3>
          <p>We call back within a working day.</p>
          {status === "done" ? (
            <InlineAlert tone="success">Thank you. Our team will review your details and get in touch with you.</InlineAlert>
          ) : (
            <>
              {status === "exists" ? <InlineAlert tone="warning" className="pa-mb-16">A request with this mobile number already exists. We will be in touch soon.</InlineAlert> : null}
              {status === "error" ? <InlineAlert tone="danger" className="pa-mb-16">Something went wrong. Please try again or call +91 91680 81867.</InlineAlert> : null}
              <div className="pa-form-grid">
                <Field label="Your name">
                  <input className="pa-input" required value={form.name} onChange={set("name")} placeholder="Full name" autoComplete="name" />
                </Field>
                <Field label="Email">
                  <input className="pa-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
                </Field>
                <Field label="Mobile">
                  <input className="pa-input" type="tel" inputMode="numeric" required value={form.mobileNumber} onChange={set("mobileNumber")} placeholder="10-digit number" autoComplete="tel-national" />
                </Field>
                <Field label="Shop name">
                  <input className="pa-input" required value={form.businessName} onChange={set("businessName")} placeholder="Shop name" autoComplete="organization" />
                </Field>
              </div>
              <Field label="Anything else (optional)">
                <textarea className="pa-textarea" maxLength={500} value={form.message} onChange={set("message")} placeholder="Location, number of printers, questions" />
              </Field>
              <Button type="submit" size="lg" block loading={status === "sending"} icon={<FiArrowRight />}>
                Request a call back
              </Button>
            </>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/** Phones: a bar with the two main actions once the hero has scrolled away. */
function MobileCta() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = () => setOn(window.scrollY > 560);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className={`stickycta ${on ? "is-on" : ""}`} aria-hidden={!on}>
      <Link to="/print" className="pa-btn pa-btn--primary" tabIndex={on ? 0 : -1}>
        <FiPrinter /> <span>Print now</span>
      </Link>
      <Link to="/get-app" className="pa-btn pa-btn--secondary" tabIndex={on ? 0 : -1}>
        <FiSmartphone /> <span>Get the app</span>
      </Link>
    </div>
  );
}
